import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav/constants';
import { Anchor, Box, Flex, Image, Skeleton, Text } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { AssetStatus, ScanEventType } from '../../../../../backend/src/types.generated';
import { useScanEventsQuery } from './graphql.generated';
import styles from './styles.module.css';

const getScanEventTypes = (type: 'bale' | 'bag' | 'audit') => {
	switch (type) {
		case 'bale':
			return [
				ScanEventType.FacilityBaleAudit,
				ScanEventType.FacilityBaleCreate,
				ScanEventType.LoadDispatched,
				ScanEventType.LoadArrival,
				ScanEventType.FacilityBaleProcessed,
			];
		case 'bag':
			return [ScanEventType.FacilityBagScan];
		case 'audit':
			return [ScanEventType.FacilityBaleAudit, ScanEventType.FacilityBagAudit];
	}
};

export const FacilityHistoryPage = ({ type }: { type: 'bale' | 'bag' | 'audit' }) => {
	const { data, loading, error } = useScanEventsQuery({
		variables: {
			filter: {
				scanEventType_in: getScanEventTypes(type),
			},
		},
	});

	// Group scan events by date
	const groupedEvents = useMemo(() => {
		if (!data?.scanEvents) return [];

		const groups: Record<string, typeof data.scanEvents> = {};

		data.scanEvents.forEach((event) => {
			if (!event?.createdAt) return;

			const date = new Date(event.createdAt);
			const dateKey = date.toLocaleDateString('en-GB'); // Format: DD/MM/YYYY

			if (!groups[dateKey]) {
				groups[dateKey] = [];
			}
			groups[dateKey].push(event);
		});

		// Sort groups by date (newest first)
		return Object.entries(groups).sort(([a], [b]) => {
			const dateA = new Date(a.split('/').reverse().join('-'));
			const dateB = new Date(b.split('/').reverse().join('-'));
			return dateB.getTime() - dateA.getTime();
		});
	}, [data]);

	const formatScanType = (type: ScanEventType | null | undefined) => {
		if (!type) return 'Unknown Scan';

		switch (type) {
			case ScanEventType.FacilityBaleCreate:
				return 'Bale Create';
			case ScanEventType.FacilityBaleInbound:
				return 'Bale Inbound';
			case ScanEventType.FacilityBaleOutbound:
				return 'Bale Outbound';
			case ScanEventType.FacilityBaleAudit:
				return 'Bale Audit';
			case ScanEventType.FacilityBagScan:
				return 'Bag Scan';
			case ScanEventType.FacilityBagAudit:
				return 'Bag Audit';
			case ScanEventType.LoadDispatched:
				return 'Load Dispatched';
			case ScanEventType.LoadArrival:
				return 'Load Arrival';
			case ScanEventType.FacilityBaleProcessed:
				return 'Bale Processed';
			default:
				return 'Unknown Scan';
		}
	};

	const formatDateTime = (dateTime: string) => {
		const date = new Date(dateTime);
		return date.toLocaleString('en-GB', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getAssetStatus = useCallback((status: AssetStatus) => {
		switch (status) {
			case AssetStatus.BagAudit:
				return 'Pending Audit';
			case AssetStatus.BagReceived:
				return 'Accepted';
			case AssetStatus.BagContaminated:
				return 'Rejected';
			case AssetStatus.BagScanned:
				return 'Pending Scan';
			case AssetStatus.BagBaled:
				return 'Baled';
			case AssetStatus.BaleCreated:
				return 'Created';
			case AssetStatus.BaleDispatched:
				return 'In Transit';
			case AssetStatus.BaleProcessed:
				return 'Processed';
			case AssetStatus.LoadDelivered:
				return 'Delivered';
			case AssetStatus.LoadInTransit:
				return 'In Transit';
		}
	}, []);

	if (loading) {
		return (
			<PrimaryLayout
				header={{ title: 'History' }}
				topNav={TopNavFor.FACILITY_SCAN_HISTORY}
				context="facility"
			>
				<Flex direction="column" w="100%" className={styles.historyContainer}>
					{Array.from({ length: 3 }).map((_, i) => (
						<Flex key={i} direction="column" className={styles.dateSection}>
							<Skeleton height={20} width={100} mb="md" />
							<Flex direction="column" className={styles.eventsList}>
								{Array.from({ length: 2 }).map((_, j) => (
									<Flex key={j} className={styles.eventItem}>
										<Skeleton circle height={20} width={20} />
										<Flex direction="column" className={styles.eventDetails}>
											<Skeleton height={16} width={200} />
										</Flex>
										<Skeleton height={14} width={80} />
										<Skeleton height={14} width={40} />
									</Flex>
								))}
							</Flex>
						</Flex>
					))}
				</Flex>
			</PrimaryLayout>
		);
	}

	if (error) {
		return (
			<PrimaryLayout
				header={{ title: 'History' }}
				topNav={TopNavFor.FACILITY_SCAN_HISTORY}
				context="facility"
			>
				<Flex direction="column" w="100%" className={styles.historyContainer}>
					<Text c="red">Error loading scan history: {error.message}</Text>
				</Flex>
			</PrimaryLayout>
		);
	}

	return (
		<PrimaryLayout
			header={{ title: 'History' }}
			topNav={TopNavFor.FACILITY_SCAN_HISTORY}
			context="facility"
		>
			<Flex direction="column" w="100%" className={styles.historyContainer}>
				{groupedEvents.length === 0 ? (
					<Text ta="center" c="dimmed" mt="xl">
						No scan events found for this facility.
					</Text>
				) : (
					groupedEvents.map(([dateKey, events]) => (
						<Flex key={dateKey} direction="column" className={styles.dateSection}>
							{/* Date Header */}
							<Text className={styles.dateHeader}>{dateKey}</Text>

							{/* Scan Events */}
							<Flex direction="column" className={styles.eventsList}>
								{events
									.sort(
										(a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
									)
									.map((event) => {
										if (!event) return null;

										const firstPhoto = event.photos?.[0]?.photo?.url;

										return (
											<Flex key={event.id} className={styles.eventItem}>
												{/* Photo or Placeholder */}
												<Box className={styles.photoContainer}>
													{firstPhoto ? (
														<Image
															src={firstPhoto}
															alt="Scan event photo"
															className={styles.eventPhoto}
														/>
													) : (
														<Box className={styles.photoPlaceholder} />
													)}
												</Box>

												{/* Event Details */}
												<Flex direction="column" gap={0} className={styles.eventDetails}>
													<Text className={styles.eventTitle}>
														{formatScanType(event.scanEventType)}
													</Text>
													{event.trackableAsset?.status && (
														<Text size="xs">{getAssetStatus(event.trackableAsset?.status)}</Text>
													)}
												</Flex>

												{event.createdAt && (
													<Text className={styles.eventTime}>
														{formatDateTime(event.createdAt)}
													</Text>
												)}

												{/* Edit Link */}
												{type !== 'bag' &&
													event.scanEventType &&
													![
														ScanEventType.FacilityBaleCreate,
														ScanEventType.FacilityBaleProcessed,
													].includes(event.scanEventType) && (
														<Anchor
															href={`/facility/edit-scan/${event.id}`}
															className={styles.editLink}
														>
															Edit
														</Anchor>
													)}
											</Flex>
										);
									})}
							</Flex>
						</Flex>
					))
				)}
			</Flex>
		</PrimaryLayout>
	);
};
